import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEsteiras } from '../api/esteirasApi';

export function useEsteiras(includeInactive = false) {
  const query = useQuery({
    queryKey: ['esteiras', includeInactive],
    queryFn: () => getEsteiras(includeInactive),
    staleTime: 5 * 60 * 1000, // Cache para 5 minutos
  });

  // Referência estável entre renders — sem isso, todo consumidor que usar
  // estas listas (ou as funções abaixo) como dependência de useEffect/useMemo
  // entra em loop de render. As funções são memoizadas desde a at-43: podem
  // entrar em arrays de dependência como dependências reais, sem eslint-disable.
  const esteiras = useMemo(() => query.data || [], [query.data]);

  const labelDe = useCallback(
    (chaveOuId?: string | null): string => {
      if (!chaveOuId) return '-';
      const found = esteiras.find((e) => e.id === chaveOuId || e.chave === chaveOuId);
      return found ? found.nome : chaveOuId;
    },
    [esteiras],
  );

  const corDe = useCallback(
    (chaveOuId?: string | null, fallback = '#939393'): string => {
      if (!chaveOuId) return fallback;
      const found = esteiras.find((e) => e.id === chaveOuId || e.chave === chaveOuId);
      return found ? found.cor : fallback;
    },
    [esteiras],
  );

  // Normaliza id, chave ou nome de esteira para a chave estável — usar em comparações.
  const chaveDe = useCallback(
    (valor?: string | null): string | null => {
      if (!valor) return null;
      const found = esteiras.find((e) => e.id === valor || e.chave === valor || e.nome === valor);
      return found ? found.chave : valor;
    },
    [esteiras],
  );

  // at-134: id, chave ou nome → o id. `null` quando a esteira não está na lista (inclusive
  // enquanto a lista carrega) — quem consulta a API por id precisa esperar, não adivinhar.
  const idDe = useCallback(
    (valor?: string | null): string | null => {
      if (!valor) return null;
      const found = esteiras.find((e) => e.id === valor || e.chave === valor || e.nome === valor);
      return found ? found.id : null;
    },
    [esteiras],
  );

  const esteirasPlanejaveis = useMemo(() => esteiras.filter((e) => e.planejavel && e.ativo), [esteiras]);
  const esteirasAtivas = useMemo(() => esteiras.filter((e) => e.ativo), [esteiras]);

  return {
    ...query,
    esteiras,
    esteirasAtivas,
    esteirasPlanejaveis,
    labelDe,
    corDe,
    chaveDe,
    idDe,
  };
}
